import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Autocomplete,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Fullscreen as ExpandIcon,
  FullscreenExit as CollapseIcon,
} from '@mui/icons-material';
import { useLazyQuery } from '@apollo/client/react';
import { useAppSelector } from '../../store/hooks';
import { GET_DECISION_NODES_BY_TREE_KEY } from '../../graphql/queries/decisionNodes.queries';
import {
  DecisionNode,
  DecisionNodesByTreeKeyResponse,
  FindDecisionNodesByTreeKeyInput,
} from '../../types/decisionNodes.types';

export interface DecisionNodeFormData {
  id?: string;
  treeKey: string;
  nodeKey: string;
  nodeType: string;
  messageTemplate: string;
  options: any | null;
  inputConfig: any | null;
  dynamicOptionsConfig: any | null;
  terminalConfig: any | null;
  contextUpdates: any | null;
  parentNodeKey: string | null;
  nextNode: string | null;
  nextNodeOnSelect: string | null;
  requiresTenant: boolean;
  displayOrder: number;
  isActive: boolean;
}

interface CreateEditDecisionNodeModalProps {
  open: boolean;
  isEdit: boolean;
  treeKey: string;
  onClose: () => void;
  onSave: (data: DecisionNodeFormData) => void;
  saving: boolean;
  saveError: string | null;
}

const NODE_TYPE_OPTIONS = ['menu', 'input', 'dynamic_select', 'terminal', 'document_search'];

const jsonEditorStyle = {
  backgroundColor: '#1e1e1e',
  color: '#d4d4d4',
  fontFamily: '"Fira Code", "Consolas", monospace',
  fontSize: '0.85rem',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: '4px',
  padding: '12px',
  width: '100%',
  resize: 'vertical' as const,
  outline: 'none',
  minHeight: '120px',
};

const expandedJsonEditorStyle = {
  ...jsonEditorStyle,
  minHeight: '400px',
};

interface JsonEditorFieldProps {
  label: string;
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
}

const JsonEditorField: React.FC<JsonEditorFieldProps> = ({ label, value, onChange, disabled }) => {
  const [expanded, setExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(value);
      onChange(JSON.stringify(parsed, null, 2));
    } catch {
      // Invalid JSON, don't format
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      // fallback
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Box>
          <Tooltip title={copySuccess ? '¡Copiado!' : 'Copiar'}>
            <IconButton size="small" onClick={handleCopy}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={expanded ? 'Contraer' : 'Expandir'}>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button size="small" onClick={handleFormat} disabled={disabled} sx={{ ml: 0.5 }}>
            Formatear JSON
          </Button>
        </Box>
      </Box>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={expanded ? expandedJsonEditorStyle : jsonEditorStyle}
      />
    </Box>
  );
};

const CreateEditDecisionNodeModal: React.FC<CreateEditDecisionNodeModalProps> = ({
  open,
  isEdit,
  treeKey,
  onClose,
  onSave,
  saving,
  saveError,
}) => {
  const { selectedNode, loading } = useAppSelector((state) => state.decisionNodes);

  const [formData, setFormData] = useState({
    nodeKey: '',
    nodeType: '' as string,
    messageTemplate: '',
    options: '',
    inputConfig: '',
    dynamicOptionsConfig: '',
    terminalConfig: '',
    contextUpdates: '',
    parentNodeKey: null as string | null,
    nextNode: null as string | null,
    nextNodeOnSelect: null as string | null,
    requiresTenant: false,
    displayOrder: 1,
    isActive: true,
  });

  const [treeNodes, setTreeNodes] = useState<DecisionNode[]>([]);

  const [fetchNodesByTreeKey] = useLazyQuery<
    { decisionNodesByTreeKey: DecisionNodesByTreeKeyResponse },
    { params: FindDecisionNodesByTreeKeyInput }
  >(GET_DECISION_NODES_BY_TREE_KEY, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (open && treeKey) {
      loadTreeNodes();
    }
  }, [open, treeKey]);

  const loadTreeNodes = async () => {
    try {
      const { data } = await fetchNodesByTreeKey({
        variables: { params: { treeKey } },
      });
      if (data?.decisionNodesByTreeKey?.success && data.decisionNodesByTreeKey.data) {
        setTreeNodes(data.decisionNodesByTreeKey.data);
      }
    } catch {
      // silently fail
    }
  };

  const formatJsonValue = (val: any): string => {
    if (val === null || val === undefined) return '';
    try {
      return typeof val === 'string' ? val : JSON.stringify(val, null, 2);
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (isEdit && selectedNode) {
      setFormData({
        nodeKey: selectedNode.nodeKey || '',
        nodeType: selectedNode.nodeType || '',
        messageTemplate: selectedNode.messageTemplate || '',
        options: formatJsonValue(selectedNode.options),
        inputConfig: formatJsonValue(selectedNode.inputConfig),
        dynamicOptionsConfig: formatJsonValue(selectedNode.dynamicOptionsConfig),
        terminalConfig: formatJsonValue(selectedNode.terminalConfig),
        contextUpdates: formatJsonValue(selectedNode.contextUpdates),
        parentNodeKey: selectedNode.parentNodeKey || null,
        nextNode: selectedNode.nextNode || null,
        nextNodeOnSelect: selectedNode.nextNodeOnSelect || null,
        requiresTenant: selectedNode.requiresTenant ?? false,
        displayOrder: selectedNode.displayOrder ?? 1,
        isActive: selectedNode.isActive ?? true,
      });
    } else if (!isEdit) {
      setFormData({
        nodeKey: '',
        nodeType: '',
        messageTemplate: '',
        options: '',
        inputConfig: '',
        dynamicOptionsConfig: '',
        terminalConfig: '',
        contextUpdates: '',
        parentNodeKey: null,
        nextNode: null,
        nextNodeOnSelect: null,
        requiresTenant: false,
        displayOrder: 1,
        isActive: true,
      });
    }
  }, [selectedNode, isEdit, open]);

  const handleChange = (field: string, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const parseJsonOrNull = (str: string): any | null => {
    if (!str.trim()) return null;
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  const handleSubmit = () => {
    const data: DecisionNodeFormData = {
      treeKey,
      nodeKey: formData.nodeKey,
      nodeType: formData.nodeType,
      messageTemplate: formData.messageTemplate,
      options: formData.nodeType === 'menu' ? parseJsonOrNull(formData.options) : null,
      inputConfig: formData.nodeType === 'input' ? parseJsonOrNull(formData.inputConfig) : null,
      dynamicOptionsConfig: formData.nodeType === 'dynamic_select' ? parseJsonOrNull(formData.dynamicOptionsConfig) : null,
      terminalConfig: formData.nodeType === 'terminal' ? parseJsonOrNull(formData.terminalConfig) : null,
      contextUpdates: parseJsonOrNull(formData.contextUpdates),
      parentNodeKey: formData.parentNodeKey || null,
      nextNode: formData.nextNode || null,
      nextNodeOnSelect: formData.nextNodeOnSelect || null,
      requiresTenant: formData.requiresTenant,
      displayOrder: formData.displayOrder,
      isActive: formData.isActive,
    };

    if (isEdit && selectedNode) {
      data.id = selectedNode.id;
    }

    onSave(data);
  };

  const isFormValid = formData.nodeKey && formData.nodeType;

  // Filter out current node from combobox options on edit
  const nodeOptions = isEdit && selectedNode
    ? treeNodes.filter((n) => n.id !== selectedNode.id)
    : treeNodes;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Nodo de Decisión' : 'Crear Nodo de Decisión'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && <Alert severity="error">{saveError}</Alert>}

            <TextField
              label="Clave del Árbol (treeKey)"
              value={treeKey}
              fullWidth
              disabled
            />

            <TextField
              label="Clave del Nodo (nodeKey)"
              value={formData.nodeKey}
              onChange={(e) => handleChange('nodeKey', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <Autocomplete
              options={NODE_TYPE_OPTIONS}
              value={formData.nodeType || null}
              onChange={(_, newValue) => handleChange('nodeType', newValue || '')}
              renderInput={(params) => (
                <TextField {...params} label="Tipo de Nodo" required />
              )}
              disabled={saving}
            />

            <TextField
              label="Plantilla de Mensaje (messageTemplate)"
              value={formData.messageTemplate}
              onChange={(e) => handleChange('messageTemplate', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={saving}
            />

            {/* Conditional JSON fields based on nodeType */}
            {formData.nodeType === 'menu' && (
              <JsonEditorField
                label="Opciones (options) - JSON"
                value={formData.options}
                onChange={(val) => handleChange('options', val)}
                disabled={saving}
              />
            )}

            {formData.nodeType === 'input' && (
              <JsonEditorField
                label="Configuración de Input (inputConfig) - JSON"
                value={formData.inputConfig}
                onChange={(val) => handleChange('inputConfig', val)}
                disabled={saving}
              />
            )}

            {formData.nodeType === 'dynamic_select' && (
              <JsonEditorField
                label="Configuración Dinámica (dynamicOptionsConfig) - JSON"
                value={formData.dynamicOptionsConfig}
                onChange={(val) => handleChange('dynamicOptionsConfig', val)}
                disabled={saving}
              />
            )}

            {formData.nodeType === 'terminal' && (
              <JsonEditorField
                label="Configuración Terminal (terminalConfig) - JSON"
                value={formData.terminalConfig}
                onChange={(val) => handleChange('terminalConfig', val)}
                disabled={saving}
              />
            )}

            {/* Always visible JSON field */}
            <JsonEditorField
              label="Actualizaciones de Contexto (contextUpdates) - JSON"
              value={formData.contextUpdates}
              onChange={(val) => handleChange('contextUpdates', val)}
              disabled={saving}
            />

            {/* Node reference comboboxes */}
            <Autocomplete
              options={nodeOptions}
              value={nodeOptions.find((n) => n.nodeKey === formData.parentNodeKey) || null}
              onChange={(_, newValue) => handleChange('parentNodeKey', newValue?.nodeKey || null)}
              getOptionLabel={(option) => `${option.nodeKey} (${option.nodeType})`}
              renderInput={(params) => (
                <TextField {...params} label="Nodo Padre (parentNodeKey)" />
              )}
              disabled={saving}
              isOptionEqualToValue={(option, value) => option.nodeKey === value.nodeKey}
            />

            <Autocomplete
              options={nodeOptions}
              value={nodeOptions.find((n) => n.nodeKey === formData.nextNode) || null}
              onChange={(_, newValue) => handleChange('nextNode', newValue?.nodeKey || null)}
              getOptionLabel={(option) => `${option.nodeKey} (${option.nodeType})`}
              renderInput={(params) => (
                <TextField {...params} label="Siguiente Nodo (nextNode)" />
              )}
              disabled={saving}
              isOptionEqualToValue={(option, value) => option.nodeKey === value.nodeKey}
            />

            <Autocomplete
              options={nodeOptions}
              value={nodeOptions.find((n) => n.nodeKey === formData.nextNodeOnSelect) || null}
              onChange={(_, newValue) => handleChange('nextNodeOnSelect', newValue?.nodeKey || null)}
              getOptionLabel={(option) => `${option.nodeKey} (${option.nodeType})`}
              renderInput={(params) => (
                <TextField {...params} label="Siguiente Nodo al Seleccionar (nextNodeOnSelect)" />
              )}
              disabled={saving}
              isOptionEqualToValue={(option, value) => option.nodeKey === value.nodeKey}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requiresTenant}
                  onChange={(e) => handleChange('requiresTenant', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Requiere Tenant"
            />

            <TextField
              label="Orden de Visualización"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => handleChange('displayOrder', parseInt(e.target.value, 10) || 1)}
              fullWidth
              disabled={saving}
              inputProps={{ min: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Activo"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !isFormValid}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditDecisionNodeModal;
